import { jwtDecode } from 'jwt-decode';

// Duality for the SPA: dev/on-prem defaults to the local backend/hub; the production
// build (vite build reads .env.production) sets these to same-origin '/' so the app
// calls the backend through the SAME Front Door hostname (no CORS, no hardcoded host).
export const LOGIN_URL = import.meta.env.VITE_LOGIN_URL ?? 'http://localhost:3000/';
export const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8000/';

// Cloud build: log in with Entra (same-origin) instead of the on-prem Login-hub form.
// Chosen at build time via VITE_AUTH_MODE (set only in the cloud .env.production) — the
// login page shows a Microsoft button and hides the credential tabs. On-prem/dev leaves
// it unset and keeps the username/password form.
export const CLOUD_AUTH = import.meta.env.VITE_AUTH_MODE === 'entra';

// export const BASE_URL = 'http://galaxy.corp.altopenergy.com:8000/';
// export const LOGIN_URL = 'http://galaxy.corp.altopenergy.com:3000/';

export const FILE_PICKER_URL = `${BASE_URL}filepicker/getNextChildren`;

// --- Access-token store (in-memory) ----------------------------------------------------------------
// The access token lives ONLY in memory, so it is cleared on page reload / browser restart — that is
// what makes a restart log the user out (the refresh token is a session cookie that also dies on
// restart). On every cold load the token is re-established by a single silent refresh against the
// HttpOnly cookie ("bootstrap"), driven by isAuthenticated()/apiFetch below. All app code reads the
// token through getAccessToken(), so this storage change is transparent to callers. The REFRESH token
// is never stored in JS at all — it lives only in the HttpOnly `altop_refresh` cookie set by the backend.
let accessToken = null;

export const getAccessToken = () => accessToken;

export const setAccessToken = (token) => {
  accessToken = token;
};

export const removeTokens = () => {
  accessToken = null;
};

export const isTokenExpired = (token) => {
  try {
    const decoded = jwtDecode(token);
    return decoded.exp < Date.now() / 1000;
  } catch (e) {
    return true;
  }
};

// True when the token expires within `bufferSeconds` (default 5 min) — used to refresh proactively
// before a request rather than waiting for a 401.
const isTokenExpiringSoon = (token, bufferSeconds = 300) => {
  try {
    const decoded = jwtDecode(token);
    return decoded.exp - Date.now() / 1000 < bufferSeconds;
  } catch (e) {
    return true;
  }
};

// --- Silent refresh (single-flight) ----------------------------------------------------------------
// Replaces the old full-page redirect to the Login hub (that round-trip was the "white page" flash).
// Refresh is now a background fetch against the HttpOnly refresh cookie; concurrent callers (multiple
// API calls, the proactive timer, several tabs) share ONE in-flight request via `refreshPromise`.
let refreshPromise = null;
let refreshTimerId = null;

export const refreshAccessToken = async () => {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const response = await fetch(`${BASE_URL}login/refresh-token/`, {
        method: 'POST',
        credentials: 'include', // send the HttpOnly altop_refresh cookie
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: '{}',
      });
      if (!response.ok) return null;
      const data = await response.json();
      const token = data && data.access_token;
      if (!token) return null;
      setAccessToken(token);
      scheduleTokenRefresh();
      try { if (authChannel) authChannel.postMessage({ type: 'refreshed' }); } catch (e) { /* ignore */ }
      return token;
    } catch (e) {
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

// Proactively refresh ~60s before the current token's exp so an active session never hits an expiry.
export const scheduleTokenRefresh = () => {
  if (refreshTimerId) {
    clearTimeout(refreshTimerId);
    refreshTimerId = null;
  }
  const token = getAccessToken();
  if (!token) return;
  let delayMs;
  try {
    const decoded = jwtDecode(token);
    delayMs = (decoded.exp - 60) * 1000 - Date.now();
  } catch (e) {
    return;
  }
  if (delayMs < 0) delayMs = 0;
  refreshTimerId = setTimeout(() => { refreshAccessToken(); }, delayMs);
};

// --- Cross-tab sync --------------------------------------------------------------------------------
// All tabs of one browser share the cookie; broadcast logout so the others don't keep a dead session.
let authChannel = null;
try {
  authChannel = new BroadcastChannel('altop_auth');
  authChannel.onmessage = (ev) => {
    if (ev && ev.data && ev.data.type === 'logout') {
      removeTokens();
      redirectToLogin();
    }
  };
} catch (e) {
  authChannel = null;
}

export const hasPermission = (requiredPermission) => {
  const token = getAccessToken();
  if (!token) return false;

  try {
    const decoded = jwtDecode(token);
    if (decoded.permissions.includes(requiredPermission) || !requiredPermission) {
      return true;
    }
    else {
      return false;
    }
  } catch (e) {
    return false;
  }
};

export const getUserEmail = () => {
  const token = getAccessToken();
  if (!token) return false;

  try {
    const decoded = jwtDecode(token);
    return decoded.email;
  } catch (e) {
    return false;
  }
};

export const getUserName = () => {
  const token = getAccessToken();
  if (!token) return false;

  try {
    const decoded = jwtDecode(token);
    return decoded.username;
  } catch (e) {
    return false;
  }
};

export const extractTokenFromURL = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get('token');
};

// Retained for the cross-site fallback app (the Azure-hosted overview), which still hands off ?token=.
// A no-op for the same-site apps that now rely on the cookie.
export const setTokenFromURL = () => {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  const iso = params.get('iso');
  const mode = params.get('mode');
  if (token) {
    setAccessToken(token);
    const newURL = window.location.pathname + window.location.hash;
    window.history.replaceState({}, document.title, newURL);
  }
  return { token, iso, mode };
};

export const redirectToLogin = () => {
  removeTokens();
  if (CLOUD_AUTH) {
    // Same-origin Entra start (BASE_URL is '/' in the cloud build). The backend
    // callback sets the refresh cookie and the app silent-refreshes to log in.
    window.location.href = `${BASE_URL}login/entra/start/?redirect=${encodeURIComponent(window.location.href)}`;
    return;
  }
  window.location.href = `${LOGIN_URL}login?redirect=${encodeURIComponent(window.location.href)}`;
};

export const logout = async () => {
  try {
    await fetch(`${BASE_URL}login/logout/`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: '{}',
    });
  } catch (e) {
    /* best effort — clear the client regardless */
  }
  removeTokens();
  if (refreshTimerId) {
    clearTimeout(refreshTimerId);
    refreshTimerId = null;
  }
  try { if (authChannel) authChannel.postMessage({ type: 'logout' }); } catch (e) { /* ignore */ }
  // Cloud: route through the backend Entra end-session endpoint so the Microsoft SSO
  // session is terminated too (a plain cookie-clear leaves it live and the next login
  // silently re-authenticates). On-prem returns to the hub login.
  window.location.href = CLOUD_AUTH ? `${BASE_URL}login/entra/logout/` : `${LOGIN_URL}login`;
};

// Valid token -> true. Missing/expired -> ONE silent refresh -> true if it yields a token, else false.
// No full-page redirect here (that was the white-page cause); the caller decides what to do on false.
export const isAuthenticated = async () => {
  const token = getAccessToken();
  if (token && !isTokenExpired(token)) {
    scheduleTokenRefresh();
    return true;
  }
  const refreshed = await refreshAccessToken();
  return !!refreshed;
};

export const apiFetch = async (url, options = {}) => {
  // Tolerate a leading slash: with same-origin BASE_URL='/', a caller passing
  // '/login/apps' would otherwise yield '//login/apps', which the browser treats as
  // protocol-relative (host "login" -> ERR_NAME_NOT_RESOLVED). Strip it so both
  // '/login/apps' and 'login/apps/' resolve the same (safe in dev too).
  const path = url.startsWith('/') ? url.slice(1) : url;
  const fetchWithToken = (token) => fetch(`${BASE_URL}${path}`, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: token ? `Bearer ${token}` : '',
    },
  });

  try {
    let token = getAccessToken();

    // Bootstrap: on a cold load / hard refresh / app-switch the in-memory token is null. Refresh ONCE
    // up front instead of sending an empty Bearer and eating a guaranteed 401. refreshAccessToken() is
    // single-flight, so concurrent first calls collapse to one network refresh.
    if (!token) {
      token = await refreshAccessToken();
    }

    // Proactive: refresh before sending if the token is about to expire, avoiding a sure 401 round-trip.
    if (token && isTokenExpiringSoon(token)) {
      const refreshed = await refreshAccessToken();
      if (refreshed) token = refreshed;
    }

    let response = await fetchWithToken(token);

    // Reactive: on 401, do one silent refresh and retry once (was: return null with no retry).
    if (response.status === 401) {
      const newToken = await refreshAccessToken();
      if (newToken) {
        response = await fetchWithToken(newToken);
      } else {
        redirectToLogin();
        return null;
      }
    }

    if (!response.ok) {
      let error = {};
      try { error = await response.json(); } catch (e) { /* non-JSON error body */ }
      throw new Error(error.error || 'Network response was not ok');
    }

    // NOTE: the Login app's apiFetch returns PARSED JSON (its long-standing contract) — Dashboard,
    // CustomDrawer and TopHeader consume `data.apps` etc. directly. This intentionally DIVERGES from the
    // canonical per-app apiFetch (which returns the raw Response and lets callers do `.json()`). Do not
    // "align" this to return `response` without also updating every Login consumer to call `.json()`.
    return await response.json();
  } catch (error) {
    console.error('API Fetch Error:', error);
    throw error;
  }
};

// --- App-specific helper preserved from pre-migration (used by the dashboard app-launcher) -----------
// Cross-app launch. r_token is intentionally dropped: the refresh token is now an HttpOnly cookie and
// is not exposed to JS — same-site apps re-establish their session from that cookie on load.
export const redirectToApp = async (iso, mode, appUrl, requiredPermission, onUnauthorized) => {
  // Open immediately while the user gesture is still active — an await before window.open would let
  // the browser treat it as a programmatic popup (blocked / background tab).
  const newTab = window.open('about:blank', '_blank');

  const token = getAccessToken();
  if (!token || isTokenExpired(token)) {
    const newToken = await refreshAccessToken();
    if (!newToken) {
      if (newTab) newTab.close();
      logout();
      return;
    }
  }

  try {
    const user = jwtDecode(getAccessToken());
    if (user && user.permissions && (user.permissions.includes(requiredPermission) || !requiredPermission)) {
      const url = new URL(appUrl);
      if (!['http://datawavedashboard.corp.altopenergy.com/'].includes(appUrl)) {
        url.searchParams.append('iso', iso);
        url.searchParams.append('mode', mode);
        // token is intentionally NOT appended: same-site apps re-establish their session from the
        // HttpOnly altop_refresh cookie via a silent refresh on load (see ProtectedRoute/isAuthenticated).
      }
      if (newTab) {
        newTab.location.href = url.toString();
        newTab.focus();
      }
    } else {
      if (newTab) newTab.close();
      onUnauthorized();
    }
  } catch (error) {
    console.error('Error decoding token:', error);
    if (newTab) newTab.close();
    logout();
  }
};
